import { z } from "zod";

import { hasUniqueValues, IanaTimeZoneSchema, IsoTimestampSchema, UUIDSchema } from "./shared";

export const SETTINGS_ID = "00000000-0000-4000-8000-000000000001" as const;
export const APP_METADATA_ID = "00000000-0000-4000-8000-000000000002" as const;
export const BACKUP_METADATA_ID = "00000000-0000-4000-8000-000000000003" as const;

export const ThemePreferenceSchema = z.enum(["light", "dark", "system"]);

export const AppearanceSettingsSchema = z
  .object({
    theme: ThemePreferenceSchema.default("system"),
    reducedMotion: z.enum(["system", "reduce", "no_preference"]).default("system"),
  })
  .strict();

export const ReadingSettingsSchema = z
  .object({
    arabicFontScale: z.number().finite().min(0.75).max(2.5).default(1),
    translationFontScale: z.number().finite().min(0.75).max(2).default(1),
    showArabic: z.boolean().default(true),
    showTranslation: z.boolean().default(true),
    showTransliteration: z.boolean().default(false),
  })
  .strict();

export const TimezoneSettingsSchema = z
  .object({
    detected: IanaTimeZoneSchema,
    override: IanaTimeZoneSchema.optional(),
  })
  .strict();

export const StoredLocationSchema = z
  .object({
    source: z.enum(["automatic", "manual"]),
    latitude: z.number().finite().min(-90).max(90),
    longitude: z.number().finite().min(-180).max(180),
    accuracyMeters: z.number().finite().nonnegative().optional(),
    capturedAt: IsoTimestampSchema,
  })
  .strict();

export const PrayerCalculationMethodSchema = z.enum([
  "muslim_world_league",
  "egyptian",
  "karachi",
  "umm_al_qura",
  "dubai",
  "qatar",
  "kuwait",
  "moonsighting_committee",
  "singapore",
  "turkey",
  "tehran",
  "north_america",
]);

const PrayerOffsetSchema = z.number().int().min(-120).max(120).default(0);

export const PrayerSettingsSchema = z
  .object({
    calculationMethod: PrayerCalculationMethodSchema.default("muslim_world_league"),
    asrMethod: z.enum(["shafi", "hanafi"]).default("shafi"),
    highLatitudeRule: z
      .enum(["middle_of_night", "seventh_of_night", "twilight_angle"])
      .default("middle_of_night"),
    adjustmentsMinutes: z
      .object({
        fajr: PrayerOffsetSchema,
        sunrise: PrayerOffsetSchema,
        dhuhr: PrayerOffsetSchema,
        asr: PrayerOffsetSchema,
        maghrib: PrayerOffsetSchema,
        isha: PrayerOffsetSchema,
      })
      .strict()
      .default({ fajr: 0, sunrise: 0, dhuhr: 0, asr: 0, maghrib: 0, isha: 0 }),
  })
  .strict();

export const FocusSettingsSchema = z
  .object({
    targetCount: z.number().int().min(1).max(50).default(12),
    includeOccasional: z.boolean().default(true),
    collectionIds: z.array(UUIDSchema).max(1_000).refine(hasUniqueValues).default([]),
    personIds: z.array(UUIDSchema).max(1_000).refine(hasUniqueValues).default([]),
  })
  .strict();

export const ContextSettingsSchema = z
  .object({
    suggestionsEnabled: z.boolean().default(true),
    visualThemesEnabled: z.boolean().default(true),
  })
  .strict();

export const PrivacySettingsSchema = z
  .object({
    lockEnabled: z.boolean().default(false),
    blurWhenHidden: z.boolean().default(false),
    autoLockMinutes: z.number().int().min(1).max(1_440).optional(),
  })
  .strict();

export const StorageSettingsSchema = z
  .object({
    backupReminderEnabled: z.boolean().default(true),
    backupReminderDays: z.number().int().min(1).max(365).default(30),
    persistentStorageRequested: z.boolean().default(false),
  })
  .strict();

const SettingsMutableShape = {
  locale: z.string().trim().min(2).max(35).default("en"),
  appearance: AppearanceSettingsSchema.default({ theme: "system", reducedMotion: "system" }),
  reading: ReadingSettingsSchema.default({
    arabicFontScale: 1,
    translationFontScale: 1,
    showArabic: true,
    showTranslation: true,
    showTransliteration: false,
  }),
  timezone: TimezoneSettingsSchema,
  location: StoredLocationSchema.optional(),
  prayer: PrayerSettingsSchema.default({
    calculationMethod: "muslim_world_league",
    asrMethod: "shafi",
    highLatitudeRule: "middle_of_night",
    adjustmentsMinutes: { fajr: 0, sunrise: 0, dhuhr: 0, asr: 0, maghrib: 0, isha: 0 },
  }),
  hijriAdjustment: z.number().int().min(-2).max(2).default(0),
  context: ContextSettingsSchema.default({ suggestionsEnabled: true, visualThemesEnabled: true }),
  focus: FocusSettingsSchema.default({
    targetCount: 12,
    includeOccasional: true,
    collectionIds: [],
    personIds: [],
  }),
  privacy: PrivacySettingsSchema.default({ lockEnabled: false, blurWhenHidden: false }),
  storage: StorageSettingsSchema.default({
    backupReminderEnabled: true,
    backupReminderDays: 30,
    persistentStorageRequested: false,
  }),
} satisfies z.ZodRawShape;

export const AppSettingsSchema = z
  .object({
    id: z.literal(SETTINGS_ID),
    ...SettingsMutableShape,
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
  })
  .strict();

export const AppSettingsUpdateSchema = z
  .object({
    locale: z.string().trim().min(2).max(35).optional(),
    appearance: AppearanceSettingsSchema.partial().strict().optional(),
    reading: ReadingSettingsSchema.partial().strict().optional(),
    timezone: TimezoneSettingsSchema.partial().strict().optional(),
    location: StoredLocationSchema.optional(),
    prayer: z
      .object({
        calculationMethod: PrayerCalculationMethodSchema.optional(),
        asrMethod: z.enum(["shafi", "hanafi"]).optional(),
        highLatitudeRule: z
          .enum(["middle_of_night", "seventh_of_night", "twilight_angle"])
          .optional(),
        adjustmentsMinutes: z
          .object({
            fajr: PrayerOffsetSchema.optional(),
            sunrise: PrayerOffsetSchema.optional(),
            dhuhr: PrayerOffsetSchema.optional(),
            asr: PrayerOffsetSchema.optional(),
            maghrib: PrayerOffsetSchema.optional(),
            isha: PrayerOffsetSchema.optional(),
          })
          .strict()
          .optional(),
      })
      .strict()
      .optional(),
    hijriAdjustment: z.number().int().min(-2).max(2).optional(),
    context: ContextSettingsSchema.partial().strict().optional(),
    focus: FocusSettingsSchema.partial().strict().optional(),
    privacy: PrivacySettingsSchema.partial().strict().optional(),
    storage: StorageSettingsSchema.partial().strict().optional(),
  })
  .strict();

export const RecordCountsSchema = z
  .object({
    personalDuas: z.number().int().nonnegative(),
    collections: z.number().int().nonnegative(),
    tags: z.number().int().nonnegative(),
    people: z.number().int().nonnegative(),
    reflections: z.number().int().nonnegative(),
    sessions: z.number().int().nonnegative(),
    sessionBlocks: z.number().int().nonnegative(),
    memorisationStates: z.number().int().nonnegative(),
  })
  .strict();

export const AppMetadataSchema = z
  .object({
    id: z.literal(APP_METADATA_ID),
    databaseVersion: z.number().int().positive(),
    bootstrapVersion: z.number().int().nonnegative(),
    installedAt: IsoTimestampSchema,
    lastOpenedAt: IsoTimestampSchema,
    onboardingCompletedAt: IsoTimestampSchema.optional(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
  })
  .strict();

export const BackupMetadataSchema = z
  .object({
    id: z.literal(BACKUP_METADATA_ID),
    lastSuccessfulBackupAt: IsoTimestampSchema.optional(),
    lastVerifiedBackupAt: IsoTimestampSchema.optional(),
    lastBackupEncrypted: z.boolean().optional(),
    lastBackupCounts: RecordCountsSchema.optional(),
    createdAt: IsoTimestampSchema,
    updatedAt: IsoTimestampSchema,
  })
  .strict();

export type AppSettings = z.output<typeof AppSettingsSchema>;
export type UpdateAppSettingsInput = z.input<typeof AppSettingsUpdateSchema>;
export type StoredLocation = z.output<typeof StoredLocationSchema>;
export type AppMetadata = z.output<typeof AppMetadataSchema>;
export type BackupMetadata = z.output<typeof BackupMetadataSchema>;
export type RecordCounts = z.output<typeof RecordCountsSchema>;

export function detectSystemTimeZone(): string {
  try {
    const candidate = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    return IanaTimeZoneSchema.parse(candidate);
  } catch {
    return "UTC";
  }
}

export function createDefaultSettings(
  timestamp: string = new Date().toISOString(),
  timezone: string = detectSystemTimeZone(),
): AppSettings {
  return AppSettingsSchema.parse({
    id: SETTINGS_ID,
    timezone: { detected: timezone },
    createdAt: timestamp,
    updatedAt: timestamp,
  });
}

export function createDefaultAppMetadata(timestamp: string = new Date().toISOString()): AppMetadata {
  return AppMetadataSchema.parse({
    id: APP_METADATA_ID,
    databaseVersion: 2,
    bootstrapVersion: 0,
    installedAt: timestamp,
    lastOpenedAt: timestamp,
    createdAt: timestamp,
    updatedAt: timestamp,
  });
}

export function createDefaultBackupMetadata(
  timestamp: string = new Date().toISOString(),
): BackupMetadata {
  return BackupMetadataSchema.parse({
    id: BACKUP_METADATA_ID,
    createdAt: timestamp,
    updatedAt: timestamp,
  });
}
